import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Clock } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', hasDropdown: true , href: '/services/adult'},
    { name: 'Team', href: '/doctors' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const servicesDropdown = [
    { 
      name: 'Adult Services', 
      href: '',
      subItems: [
        { name: 'Anxiety Disorder', href: '/services/anxiety' },
        { name: 'Depression', href: '/services/depression' },
        { name: 'Family Counseling', href: '/services/family-counseling' },
        { name: 'Bipolar Disorders', href: '/services/bipolar-disorder' },
        { name: 'Obsessive Disorder', href: '/services/obsessive-disorder' },
        { name: 'Anger Management', href: '/services/anger-management' },
        { name: 'Personality Disorders', href: '/services/personality-disorders' },
        { name: 'Behavioral Addictions', href: '/services/behavioral-addictions' },
        { name: 'Psychosis Problem', href: '/services/psychosis-problem' },
        { name: 'LGBT Psychiatry', href: '/services/lgbt-psychiatry' },
        { name: 'De-Addiction Counseling', href: '/services/de-addiction-counseling' },
        { name: 'Geriatrics Mental Health', href: '/services/geriatrics-mental-health' },
        { name: 'Self-Esteem Building', href: '/services/self-esteem-building' },
        { name: 'Work-Related Stress', href: '/services/work-related-stress' },
        { name: 'Women\'s Mental Health', href: '/services/womens-mental-health' },
        { name: 'Severe Psychiatric Disorders', href: '/services/severe-psychiatric-disorders' }
      ]
    },
    { 
      name: 'Child Services', 
      href: '',
      subItems: [
        { name: 'Individual Play Therapy', href: '/services/individual-play-therapy' },
        { name: 'Speech Therapy', href: '/services/speech-therapy' },
        { name: 'Occupational Therapy', href: '/services/occupational-therapy' },
        { name: 'Special Education', href: '/services/special-education' },
        { name: 'Group Therapy', href: '/services/group-therapy' },
        { name: 'Psychological Testing', href: '/services/psychological-testing' },
        { name: 'Teen-Sensitive Services', href: '/services/teen-sensitive-services' },
        { name: 'Trauma Outreach', href: '/services/trauma-outreach' },
        { name: 'Crisis Intervention And Brief Therapy', href: '/services/crisis-intervention' },
        { name: 'Eating Disorder Treatment', href: '/services/eating-disorder-treatment' },
        { name: 'Caregiver Consultation', href: '/services/caregivers-consultation' },
        { name: 'Assessment Program', href: '/services/assessment-program' },
        { name: 'Infant-Parent Programs', href: '/services/infant-parent-programs' },
        { name: 'Information & Referrals', href: '/services/information-referral' }
      ]
    },
    { 
      name: 'Inpatient Programs', 
      href: '',
      subItems: [
        { name: 'Inpatient Care', href: '/services/inpatient-care' }
      ]
    },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center">
            <Link className="flex-shrink-0 flex items-center">
              <div className="flex items-center space-x-3">
                <img 
                  src="/assets/img/logo.webp" 
                  alt="Solace Neuro Logo" 
                  className="h-12 w-auto"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-5">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div 
                    className="group"
                    onMouseEnter={() => setIsServicesDropdownOpen(true)}
                    onMouseLeave={() => setIsServicesDropdownOpen(false)}
                  >
                    <Link
                      to={item.href}
                      className={`${
                        isActive(item.href)
                          ? 'text-primary border-b-2 border-primary'
                          : 'text-gray-700 hover:text-primary'
                      } px-2 py-2 text-sm font-medium transition-colors whitespace-nowrap`}
                    >
                      {item.name}
                    </Link>
                    
                    {/* Dropdown Menu */}
                    <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-[55rem] bg-white rounded-lg shadow-xl border border-gray-200 z-50 transition-all duration-200 ${
                      isServicesDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}>
                      <div className="py-4">
                        <div className="grid grid-cols-3 gap-4">
                          {servicesDropdown.map((service, index) => (
                            <div key={service.name} className={`px-4 py-2 ${index > 0 ? 'border-l border-gray-300' : ''}`}>
                              <div className="block text-sm font-semibold" style={{ color: '#00A785', marginBottom: '8px' }}>
                                {service.name}
                              </div>
                              <div className="space-y-2">
                                {service.subItems && service.subItems.map((item) => (
                                  <Link
                                    key={item.name || item}
                                    to={item.href || service.href}
                                    className="block text-sm text-gray-600 hover:text-primary transition-colors"
                                  >
                                    {item.name || item}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`${
                      isActive(item.href)
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-gray-700 hover:text-primary'
                    } px-3 py-2 text-sm font-medium transition-colors`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.hasDropdown ? (
                  <div>
                    <button
                      onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                      className={`${
                        isActive(item.href)
                          ? 'text-primary bg-primary-50 border-l-4 border-primary'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                      } w-full text-left pl-3 pr-4 py-2 text-base font-medium`}
                    >
                      {item.name}
                    </button>
                    
                    {/* Mobile Dropdown Menu */}
                    {isServicesDropdownOpen && (
                      <div className="bg-gray-50 border-l-4 border-primary">
                        {servicesDropdown.map((service) => (
                          <div key={service.name}>
                            <div className="block pl-8 pr-4 py-2 text-sm font-semibold text-gray-900">
                              {service.name}
                            </div>
                            <div className="bg-gray-100">
                              {service.subItems && service.subItems.map((item) => (
                                <Link
                                  key={item.name || item}
                                  to={item.href || service.href}
                                  className="block pl-12 pr-4 py-1 text-sm text-gray-600 hover:text-primary hover:bg-gray-200 transition-colors"
                                  onClick={() => {
                                    setIsServicesDropdownOpen(false);
                                    setIsOpen(false);
                                  }}
                                >
                                  {item.name || item}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive(item.href)
                        ? 'text-primary bg-primary-50 border-l-4 border-primary'
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    } block pl-3 pr-4 py-2 text-base font-medium`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
